<template>
    <section class="bo-card">
        <FormTabHeader
            icon="fal fa-tasks"
            :title="$t('backoffice_infos.sections.statuses')"
        />
        <div class="bo-body">
            <div
                v-for="form in forms"
                :key="'status-' + form.id"
                class="bo-form-block"
                :class="{ 'bo-form-block--open': openedId === form.id }"
            >
                <div class="bo-form-block-header" @click="toggle(form.id)">
                    <div class="bo-form-block-title">
                        <i class="fal fa-chevron-right bo-chevron"></i>
                        <i class="fal fa-file-alt bo-form-icon"></i>
                        <span class="bo-form-name">{{ form.name }}</span>
                        <span class="bo-form-meta">
                            {{ form.statuses ? form.statuses.length : 0 }} {{ $t('backoffice_infos.statuses_count') }}
                        </span>
                    </div>
                    <router-link
                        :to="`/forms/${form.id}`"
                        class="bo-action-btn"
                        @click.native.stop
                    >
                        <i class="fal fa-eye"></i>
                        {{ $t('form_types.view') }}
                    </router-link>
                </div>
                <BmvCollapse :open="openedId === form.id">
                    <div class="bo-form-block-body">
                        <div v-if="form.statuses && form.statuses.length" class="bo-table-wrapper">
                            <table class="bo-table">
                                <thead>
                                    <tr>
                                        <th class="bo-table-th-narrow">{{ $t('forms.status.icon') }}</th>
                                        <th>{{ $t('forms.status.name') }}</th>
                                        <th>{{ $t('forms.status.default') }}</th>
                                        <th>{{ $t('forms.status.active') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="status in form.statuses" :key="status.id">
                                        <td><i :class="status.icon" class="bo-status-icon"></i></td>
                                        <td>{{ status.name }}</td>
                                        <td>
                                            <span class="bo-pill" :class="status.default ? 'bo-pill--yes' : 'bo-pill--neutral'">
                                                {{ status.default ? $t('common.yes') : $t('common.no') }}
                                            </span>
                                        </td>
                                        <td>
                                            <span class="bo-pill" :class="status.active ? 'bo-pill--yes' : 'bo-pill--no'">
                                                {{ status.active ? $t('common.yes') : $t('common.no') }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p v-else class="bo-empty">{{ $t('backoffice_infos.no_statuses') }}</p>
                    </div>
                </BmvCollapse>
            </div>
        </div>
    </section>
</template>

<script>
import FormTabHeader from '@/components/Model/Form/FormComponents/FormTabHeader'
import BmvCollapse from '@/components/UI/v2/BmvCollapse.vue'

export default {
    name: 'BackofficeStatuses',
    components: { FormTabHeader, BmvCollapse },
    props: {
        forms: { type: Array, required: true }
    },
    data() {
        return {
            openedId: null
        }
    },
    methods: {
        toggle(formId) {
            this.openedId = this.openedId === formId ? null : formId
        }
    }
}
</script>

<style scoped>
.bo-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.bo-body {
    padding: var(--v2-space-4) var(--v2-space-5);
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
}

.bo-form-block {
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    overflow: hidden;
}

.bo-form-block-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    padding: 12px 16px;
    background: var(--v2-surface-1);
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.bo-form-block-header:hover {
    background: var(--v2-surface-2);
}

.bo-form-block-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex-wrap: wrap;
}

.bo-chevron {
    font-size: 0.75rem;
    color: var(--v2-text-3);
    width: 10px;
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.bo-form-block--open .bo-chevron {
    transform: rotate(90deg);
    color: var(--v2-primary);
}

.bo-form-icon {
    font-size: 0.85rem;
    color: var(--v2-primary);
}

.bo-form-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.bo-form-meta {
    font-size: 0.72rem;
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-medium);
    font-variant-numeric: tabular-nums;
}

.bo-form-block-body {
    padding: var(--v2-space-4);
    background: var(--v2-surface-0);
    border-top: 1px solid var(--v2-border-subtle);
}

.bo-table-wrapper {
    overflow-x: auto;
}

.bo-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--v2-text-xs-size);
}

.bo-table th {
    text-align: left;
    padding: 8px 12px;
    background: var(--v2-surface-1);
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-size: 0.68rem;
    border-bottom: 1px solid var(--v2-border);
}

.bo-table td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--v2-border-subtle);
    color: var(--v2-text-2);
    vertical-align: middle;
}

.bo-table tr:last-child td {
    border-bottom: none;
}

.bo-table-th-narrow {
    width: 48px;
}

.bo-status-icon {
    color: var(--v2-primary);
    font-size: 0.9rem;
}

.bo-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: var(--v2-font-weight-semibold);
}

.bo-pill--yes {
    background: rgba(16, 185, 129, 0.12);
    color: #059669;
}

.bo-pill--no {
    background: rgba(239, 68, 68, 0.12);
    color: #dc2626;
}

.bo-pill--neutral {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

.bo-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 10px;
    border-radius: var(--v2-radius-sm);
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    color: var(--v2-text-2);
    text-decoration: none !important;
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    flex-shrink: 0;
}

.bo-action-btn i {
    font-size: 0.72rem;
}

.bo-action-btn:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.bo-empty {
    margin: 0;
    padding: 10px 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-4);
    font-style: italic;
}

@media (max-width: 768px) {
    .bo-body {
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .bo-form-block-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
}
</style>
